import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import React, { ChangeEvent, useEffect, useState } from "react";
import { CreateLicenseDto, Order } from "../../api/api-generated";
import {
  useCreateLicenseMutation,
  useFindAllCompaniesQuery,
  useFindAllHardwareQuery,
  useFindAllModulesQuery,
  useFindAllOrdersQuery,
  useFindAllSoftwareQuery,
  useParseLicensesMutation,
} from "../../api/api-hooks";
import { TrimmedCompany } from "../../api/api-types";

interface Props {
  dialogOpen: boolean;
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  company?: TrimmedCompany;
}

interface FormState {
  company: TrimmedCompany | null;
  order: Order | null;
}

const ImportDialog = ({ dialogOpen, setDialogOpen, company }: Props) => {
  const [formState, setFormState] = useState<FormState>({
    company: null,
    order: null,
  });
  const [licensesToAdd, setLicensesToAdd] = useState<CreateLicenseDto[]>([]);
  const { data: companies } = useFindAllCompaniesQuery({});
  const { data: orders } = useFindAllOrdersQuery({});
  const { data: hardware } = useFindAllHardwareQuery({});
  const { data: softwareTypes } = useFindAllSoftwareQuery({});
  const { data: modules } = useFindAllModulesQuery({});
  const [createLicense] = useCreateLicenseMutation();
  const [upload, { isLoading, isSuccess, isError, reset }] =
    useParseLicensesMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setFormState({ company: company ? company : null, order: null });
  }, [company, dialogOpen]);

  const closeForm = () => {
    setDialogOpen(false);
    setFormState({ company: null, order: null });
    setLicensesToAdd([]);
    reset();
  };

  const handleUploadLicense = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!softwareTypes || !modules || !e.target.files) return;
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (e) => {
      const text = e.target?.result;
      if (!text || typeof text !== "string") return;
      const textObj = JSON.parse(text);
      const result = await upload({ parseLicenseDto: { licenses: textObj } });
      if ("data" in result) {
        const spectras = softwareTypes.find(
          (softwareType) => softwareType.type.toLowerCase() === "spectras"
        );
        if (!spectras) return;
        const responseData = result.data;
        const newLicenses: CreateLicenseDto[] = [];
        for (const licenseData of responseData) {
          const foundHardware = hardware?.find(
            (item) => item.serial === licenseData.analyzer.serial
          );
          if (!foundHardware || !formState.company) return;
          newLicenses.push({
            companyId: formState.company.id,
            hardwareId: foundHardware.id,
            license: licenseData.analyzer.baseLicense,
            softwareId: spectras.id,
            moduleId: null,
          });
          for (const module of licenseData.modules) {
            const associatedModule = modules.find(
              (item) => item.moduleName === module.type
            );
            if (!associatedModule) continue;
            newLicenses.push({
              companyId: formState.company.id,
              hardwareId: foundHardware.id,
              license: module.moduleLicense,
              softwareId: spectras.id,
              moduleId: associatedModule.id,
            });
          }
        }
        setLicensesToAdd(newLicenses);
      }
    };
    reader.readAsText(file);
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      for await (const license of licensesToAdd) {
        await createLicense({ createLicenseDto: license });
      }
      setIsSubmitting(false);
      closeForm();
    } catch (e) {
      setIsSubmitting(false);
      closeForm();
    }
  };

  if (companies === undefined || orders === undefined) return null;

  const filteredOrders = orders.filter((order) => !order.completed);

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={dialogOpen}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle>Import Spectras Licenses</DialogTitle>
      <DialogContent>
        {isSubmitting ? (
          <Grid container justifyContent="center" alignItems="center">
            <CircularProgress />
          </Grid>
        ) : (
          <Grid container>
            <Grid container item xs direction="column" spacing={1}>
              <Grid item>
                <Autocomplete
                  onChange={(e, value) =>
                    setFormState((state) => ({ ...state, company: value }))
                  }
                  value={formState.company}
                  options={companies}
                  getOptionLabel={(company) => company.companyName}
                  style={{ width: 300, marginTop: ".5rem" }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Company"
                      name="company"
                      variant="outlined"
                      id="company"
                    />
                  )}
                />
              </Grid>
              <Grid item>
                <Autocomplete
                  onChange={(event, value) => {
                    setFormState((state) => ({ ...state, order: value }));
                  }}
                  value={formState.order}
                  options={filteredOrders}
                  getOptionLabel={(order) =>
                    order.purchaseOrderName
                      ? order.purchaseOrderName
                      : order.orderDate
                  }
                  style={{ width: 300 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Order"
                      variant="outlined"
                    />
                  )}
                />
              </Grid>
              <Grid container item alignItems="center" spacing={1}>
                <Grid item>
                  <input
                    style={{ display: "none" }}
                    id="outlined-button-file"
                    type="file"
                    accept=".lic"
                    onChange={handleUploadLicense}
                  />
                  <label htmlFor="outlined-button-file">
                    <Button
                      disabled={!formState.company}
                      variant="outlined"
                      color="primary"
                      component="span"
                    >
                      Upload License
                    </Button>
                  </label>
                </Grid>
                {isLoading && (
                  <Grid item>
                    <CircularProgress></CircularProgress>
                  </Grid>
                )}
                {isSuccess && (
                  <Grid item>
                    <Typography variant="body2">Upload successful.</Typography>
                  </Grid>
                )}
                {isError && (
                  <Grid item>
                    <Typography variant="body2">Upload failed.</Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid container item xs direction="column" spacing={1}>
              <Grid item>
                <Typography>Items to Add:</Typography>
                {licensesToAdd.map((license) => (
                  <Typography sx={{ pl: "1rem" }}>
                    {hardware?.find((item) => item.id === license.hardwareId)
                      ?.serial +
                      ": " +
                      (license.moduleId
                        ? modules?.find((item) => item.id === license.moduleId)
                            ?.moduleName
                        : "Core")}
                  </Typography>
                ))}
              </Grid>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSubmit} disabled={licensesToAdd.length === 0}>
          Submit
        </Button>
        <Button onClick={closeForm}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportDialog;
